import * as XLSX from 'xlsx';
import { TranslationFile } from '../types';

interface ExportData {
    sourceSegments: string[];
    aiSegments: string[];
    benchmarkSegments: string[];
    auditScores?: number[];
    fileName: string;
}

/**
 * Export comparison report to Excel
 */
export function exportComparisonToExcel(data: ExportData): void {
    const { sourceSegments, aiSegments, benchmarkSegments, auditScores, fileName } = data;

    // Build rows
    const rows = sourceSegments.map((source, i) => ({
        '#': i + 1,
        'Source (EN)': source,
        'AI Translation': aiSegments[i] || '',
        'Benchmark (VI)': benchmarkSegments[i] || '',
        'Score': auditScores?.[i] ?? '-',
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(rows);

    // Set column widths
    ws['!cols'] = [
        { wch: 5 },   // #
        { wch: 50 },  // Source
        { wch: 50 },  // AI Translation
        { wch: 50 },  // Benchmark
        { wch: 8 },   // Score
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Comparison');

    // Generate filename
    const exportName = fileName.replace(/\.[^.]+$/, '') + '_comparison.xlsx';

    // Download
    XLSX.writeFile(wb, exportName);
}

/**
 * Export full project report to Excel (multiple files)
 */
export function exportProjectToExcel(files: TranslationFile[], projectName: string): void {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryRows = files.map(f => ({
        'File': f.fileName,
        'Status': f.translationStatus,
        'Audit Status': f.auditStatus,
        'Score': f.auditResult?.overallScore ?? '-',
        'Errors': f.auditResult?.criticalErrors?.length ?? 0,
    }));

    const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
    summaryWs['!cols'] = [
        { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 8 }
    ];
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    // Errors sheet (aggregated)
    const allErrors = files.flatMap(f =>
        (f.auditResult?.criticalErrors || []).map(e => ({
            'File': f.fileName,
            'Severity': e.severity,
            'Original': e.original,
            'AI Choice': e.ai_choice,
            'Better Choice': e.better_choice,
            'Reason': e.reason,
        }))
    );

    if (allErrors.length > 0) {
        const errorsWs = XLSX.utils.json_to_sheet(allErrors);
        errorsWs['!cols'] = [
            { wch: 25 }, { wch: 12 }, { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 40 }
        ];
        XLSX.utils.book_append_sheet(wb, errorsWs, 'Errors');
    }

    // Export
    const exportName = (projectName || 'project') + '_report.xlsx';
    XLSX.writeFile(wb, exportName);
}
